/** Strip HTML tags for card previews after translation. */
export function htmlToPlainText(value: string): string {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Strip HTML tags but keep line breaks (single and blank-line); pair with `whitespace-pre-line`. */
export function htmlToPlainParagraphs(value: string): string {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function hasBulletContent(value: string | string[] | undefined): boolean {
  if (!value) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => item.trim().length > 0);
  }

  return value.trim().length > 0;
}

export function specLabel(spec: { key?: string; label?: string }): string {
  return spec.key ?? spec.label ?? '';
}

/** Split free-text "intro sentence + `\n1. ...\n2. ...`" installation copy into intro + numbered steps. */
export function parseNumberedSteps(text: string): { intro?: string; steps: string[] } {
  const stepPattern = /^\d+\.\s+(.*)$/;
  const introLines: string[] = [];
  const steps: string[] = [];
  for (const rawLine of text.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      continue;
    }
    const match = line.match(stepPattern);
    if (match) {
      steps.push(match[1]);
    } else if (steps.length === 0) {
      introLines.push(line);
    }
  }

  return { intro: introLines.length ? introLines.join(' ') : undefined, steps };
}

/** Alt text for a product's own photos: its translated name plus code, unless the name already contains the code. */
export function productImageAlt(translatedName: string, code: string): string {
  return translatedName.toLowerCase().includes(code.toLowerCase()) ? translatedName : `${translatedName} ${code}`;
}

/** Uppercase the first letter, so a translated name like "univerzální vrut Killich" still reads as a heading. */
export function capitalizeFirst(text: string): string {
  return text.charAt(0).toLocaleUpperCase() + text.slice(1);
}

/**
 * First sentence of a plain-text teaser. A sentence ends at `.`, `!` or `?` followed by a capitalised word, so
 * "stupně 3 pro …" or an abbreviation like "např. topení" does not cut it short.
 */
export function firstSentence(text: string): string {
  return text.trim().split(/(?<=[.!?])\s+(?=\p{Lu})/u)[0];
}
